import React, { type PropsWithChildren } from 'react';

import { IntlProvider, useIntl } from 'react-intl-next';
import {
	createActionsHook,
	createContainer,
	createHook,
	createStore,
	createSubscriber,
	type StoreActionApi,
} from 'react-sweet-state';

import {
	type ContainerProps,
	type FlagService,
	type Locale,
	type OriginalContainerProps,
	type State,
} from '../common/utils/types';

import * as actions from './actions';

export const Store = createStore<State, typeof actions>({
	name: 'global-context',
	initialState: {
		reporter: undefined,
	},
	actions,
});

export const GlobalContextContainer = ({
	flagService,
	reporter,
	children,
	messages,
}: PropsWithChildren<ContainerProps>) => {
	const { locale } = useIntl();

	return (
		<OriginalGlobalContextContainer reporter={reporter} isGlobal flagService={flagService}>
			<IntlProvider locale={locale} messages={messages && messages[locale as Locale]}>
				{children}
			</IntlProvider>
		</OriginalGlobalContextContainer>
	);
};

const OriginalGlobalContextContainer = createContainer<
	State,
	typeof actions,
	OriginalContainerProps
>(Store, {
	onInit:
		() =>
		async (
			{ getState, setState }: StoreActionApi<State>,
			{ flagService }: OriginalContainerProps,
		) => {
			!getState().flagService && setState({ flagService });
		},
});

export const GlobalContextSubscriber = createSubscriber<State, {}>(Store);

export const useExternalFlagService = createHook<State, {}, FlagService | undefined>(Store, {
	selector: (state) => state.flagService,
});

export const useGlobalContextActions = createActionsHook<State, typeof actions>(Store);
